import {createContext, type PropsWithChildren, use, useCallback, useMemo} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {dataRetryPolicy, fetchBackend} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import {MyAccount} from "@/lib/types";

const AccountContext = createContext<{
    account?: MyAccount;
    accountLoading: boolean;
    accountError: Error | null;
    refreshAccount: () => any;
    isFriend: (user_id: number) => boolean,
}>({
    account: undefined,
    accountLoading: true,
    accountError: null,
    refreshAccount: () => {
    },
    isFriend: (_) => false,
});

export function useAccount() {
    const value = use(AccountContext);
    if (!value) {
        throw new Error('useAccount must be wrapped in a <AccountProvider />');
    }
    return value;
}

export function AccountProvider({children}: PropsWithChildren) {
    const queryClient = useQueryClient();
    const {session, ...sessionParams} = useSession();

    const queryKey = useMemo(() => ['account', session], [session]);

    const refreshAccount = useCallback(async () => {
        await queryClient.invalidateQueries({queryKey});
    }, [queryClient, queryKey]);

    const {data, isLoading, error} = useQuery<MyAccount>({
        queryKey,
        queryFn: () => fetchBackend(`/account`, "GET", session),
        retry: dataRetryPolicy,
        enabled: !!session && !sessionParams.isLoading,
    })

    const isFriend = useCallback((user_id: number) => {
        if (!data) {
            return false;
        }
        return binarySearch(data.friend_ids, user_id) !== null;
    }, [data])

    return (
        <AccountContext.Provider
            value={{
                account: data,
                accountLoading: isLoading,
                accountError: error,
                refreshAccount,
                isFriend,
            }}>
            {children}
        </AccountContext.Provider>
    );
}

function binarySearch<T>(arr: T[], target: T, start = 0, end = arr.length - 1): number | null {
    let mid = Math.floor((start + end) / 2);

    if (start > end) {
        return null;
    }

    if (arr[mid] === target) {
        return mid;
    } else if (arr[mid] < target) {
        return binarySearch(arr, target, mid + 1, end);
    } else {
        return binarySearch(arr, target, start, mid - 1);
    }
}